import type { DailyData, MonthCell, Trade } from '../../types';
import { loadImportedTrades } from '../../utils/importedTradesStorage';
import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';
import { buildTenantStorageKey } from '../storage/tenantStorage';
import { migrateOwnedLegacyPrivateValue, privateTradingStorageKey, readPrivateTradingValue, writePrivateTradingValue } from '../storage/privateTradingStorage';
import { normalizeTradeAssetClassification } from './canonicalAssetClass';
import { isIndexOrCfdInstrument } from '../../constants/indexInstruments';
import { addWinLossCount, calculateWinRatePercent } from './winRate';

export const CUSTOM_TRADES_STORAGE_KEY = 'private:custom-trades';
export const CUSTOM_TRADES_UPDATED_EVENT = 'custom-trades-updated';

const CUSTOM_TRADES_STORAGE_DOMAIN = 'custom-trades';
const CUSTOM_TRADES_STORAGE_KEY_LEGACY = buildTenantStorageKey('journal.customTrades.v1');

export type AnyTrade = Trade & Record<string, any>;

type AggregatedDay = {
  pnl: number;
  trades: number;
  volume: number;
  wins: number;
  losses: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const pad = (value: number) => String(value).padStart(2, '0');

const toDateKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const coerceNumber = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const MIN_VALID_CLOSE_MS = new Date(2000, 0, 1).getTime();

const OPEN_STATUS_VALUES = new Set(['open', 'opened', 'active', 'live', 'pending']);
const CLOSED_STATUS_VALUES = new Set(['closed', 'close', 'completed', 'complete', 'done', 'realized']);

const EXIT_PRICE_FIELDS = ['exitPrice', 'avgExitPrice', 'avgExit', 'exit_price'] as const;
const CLOSE_TIME_FIELDS = [
  'closeTimestampMs',
  'exitTimestampMs',
  'exitTimestamp',
  'exitTime',
  'closeTime',
  'closedAt',
  'closed_at_utc',
  'closeDate',
  'close_time',
  'close_at',
  'closeAt',
  'closeTimestamp',
  'exit_at',
  'exitAt',
] as const;

const REALIZED_PNL_FIELDS = [
  'brokerReportedPnl',
  'broker_reported_pnl',
  'brokerPnl',
  'rowRealizedNet',
  'realizedNetPnl',
  'realized_net_pnl',
  'realizedPnl',
  'realized_pnl',
  'netPnl',
  'net_pnl',
  'calculatedPnl',
  'calculated_pnl',
  'pnl',
] as const;

function hasOwnField(row: Record<string, any>, field: string): boolean {
  return Object.prototype.hasOwnProperty.call(row, field);
}

function isBlankValue(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

function hasFiniteField(row: Record<string, any>, fields: readonly string[]): boolean {
  return fields.some((field) => {
    if (!hasOwnField(row, field)) return false;
    const numeric = Number(row[field]);
    return Number.isFinite(numeric);
  });
}

function hasBlankField(row: Record<string, any>, fields: readonly string[]): boolean {
  return fields.some((field) => hasOwnField(row, field) && isBlankValue(row[field]));
}

function hasUsableCloseTime(row: Record<string, any>): boolean {
  return CLOSE_TIME_FIELDS.some((field) => {
    if (!hasOwnField(row, field)) return false;
    const timestampMs = normalizeTimestampToMs(row[field]);
    return timestampMs != null && timestampMs >= MIN_VALID_CLOSE_MS;
  });
}

const normalizeArray = (value: unknown): AnyTrade[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry) => entry && typeof entry === 'object') as AnyTrade[];
};

export function loadCustomTrades(): AnyTrade[] {
  if (typeof window === 'undefined') return [];
  try {
    if (!privateTradingStorageKey(CUSTOM_TRADES_STORAGE_DOMAIN)) return [];
    const parsed = readPrivateTradingValue<unknown>(CUSTOM_TRADES_STORAGE_DOMAIN)
      ?? migrateOwnedLegacyPrivateValue<unknown>(CUSTOM_TRADES_STORAGE_DOMAIN, [CUSTOM_TRADES_STORAGE_KEY_LEGACY, 'journal.customTrades.v1'])
      ?? [];
    return normalizeArray(parsed).map((trade) => normalizeTradeAssetClassification({ ...trade, isCustom: true }));
  } catch {
    return [];
  }
}

export function saveCustomTrades(trades: unknown[], options: { source?: string } = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (!writePrivateTradingValue(CUSTOM_TRADES_STORAGE_DOMAIN, trades)) return;
    window.dispatchEvent(new CustomEvent(CUSTOM_TRADES_UPDATED_EVENT, { detail: { total: trades.length, ...(options.source ? { source: options.source } : {}) } }));
  } catch {}
}

export function getTradeTimestampMs(trade: AnyTrade): number | null {
  const candidates = [
    trade?.openTimestampMs,
    trade?.timestamp,
    trade?.entryTime,
    trade?.entryTimestamp,
    trade?.executedAt,
    trade?.filledAt,
    trade?.openTime,
    trade?.time,
    trade?.date,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeTimestampToMs(candidate);
    if (normalized != null) return normalized;
  }

  const rawDate = String(trade?.date ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    const parsed = new Date(`${rawDate}T00:00:00`);
    return Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
  }

  return null;
}

export function getTradeDateKey(trade: AnyTrade): string | null {
  const timestampMs = getTradeTimestampMs(trade);
  if (timestampMs != null) {
    return toDateKey(new Date(timestampMs));
  }

  const rawDate = String(trade?.date ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) return rawDate;

  return null;
}

function roundToCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function getTradePriceBasedPnl(trade: AnyTrade): number | null {
  if (normalizeTradeStatus(trade) === 'open') return null;
  if (isIndexOrCfdInstrument(trade?.symbol, trade?.instrumentType ?? trade?.instrument, trade)) {
    return null;
  }

  const entryPrice = Number(
    trade?.entryPrice ?? trade?.avgEntryPrice ?? trade?.avgEntry ?? trade?.entry_price ?? trade?.price ?? trade?.avgPrice ?? trade?.avg_price
  );
  const exitPrice = Number(
    trade?.exitPrice ?? trade?.avgExitPrice ?? trade?.avgExit ?? trade?.exit_price
  );
  const quantity = Math.abs(Number(trade?.quantity ?? trade?.qty ?? trade?.size ?? trade?.volume ?? trade?.shares ?? 0));

  if (!Number.isFinite(entryPrice) || !Number.isFinite(exitPrice) || quantity <= 0) return null;

  const side = normalizeTradeSide(trade);
  const gross = side === 'SHORT'
    ? (entryPrice - exitPrice) * quantity
    : (exitPrice - entryPrice) * quantity;
  const costs = extractTradeCost(trade);
  return roundToCurrency(gross - costs);
}

function isAuthoritativeNetField(field: string): boolean {
  return [
    'brokerReportedPnl',
    'broker_reported_pnl',
    'brokerPnl',
    'rowRealizedNet',
    'realizedNetPnl',
    'realized_net_pnl',
    'realizedPnl',
    'realized_pnl',
    'netPnl',
    'net_pnl',
  ].includes(field);
}

const DIRECT_GROSS_PNL_FIELDS = [
  'grossPnl',
  'gross_pnl',
  'realizedGrossPnl',
  'realized_gross_pnl',
  'rowRealizedGross',
  'realized_gross',
  'pnlGross',
] as const;

/**
 * Resolves the genuine gross P&L (before commissions and fees) for an individual closed trade.
 *
 * Rules:
 * 1. Exclude open trades (returns null).
 * 2. Checks explicit direct gross fields first (e.g. grossPnl, gross_pnl, realizedGrossPnl, rowRealizedGross).
 * 3. For non-Index/CFD trades, derives gross from entry/exit prices and quantity if available.
 * 4. Derives gross from realized net P&L + extracted trade costs (commissions/fees) if costs are known.
 * 5. For Index CFDs, never reconstructs P&L via stock-share arithmetic.
 *    If direct gross or costs are available, uses them; otherwise returns null (does not invent gross).
 */
export function resolveTradeGrossPnl(trade: AnyTrade): number | null {
  if (normalizeTradeStatus(trade) === 'open') return null;

  for (const field of DIRECT_GROSS_PNL_FIELDS) {
    const candidate = trade?.[field];
    if (candidate == null || candidate === '') continue;
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) return roundToCurrency(numeric);
  }

  const isIndexCfd = isIndexOrCfdInstrument(trade?.symbol, trade?.instrumentType ?? trade?.instrument, trade);

  if (!isIndexCfd) {
    const entryPrice = Number(
      trade?.entryPrice ?? trade?.avgEntryPrice ?? trade?.avgEntry ?? trade?.entry_price ?? trade?.price ?? trade?.avgPrice ?? trade?.avg_price
    );
    const exitPrice = Number(
      trade?.exitPrice ?? trade?.avgExitPrice ?? trade?.avgExit ?? trade?.exit_price
    );
    const quantity = Math.abs(Number(trade?.quantity ?? trade?.qty ?? trade?.size ?? trade?.volume ?? trade?.shares ?? 0));

    if (Number.isFinite(entryPrice) && Number.isFinite(exitPrice) && quantity > 0) {
      const side = normalizeTradeSide(trade);
      const gross = side === 'SHORT'
        ? (entryPrice - exitPrice) * quantity
        : (exitPrice - entryPrice) * quantity;
      return roundToCurrency(gross);
    }
  }

  // If net realized P&L is known and costs are recorded, derive gross = net + costs
  const costs = extractTradeCost(trade);
  const realizedNet = resolveTradeRealizedPnl(trade);
  if (realizedNet != null) {
    if (costs > 0) {
      return roundToCurrency(realizedNet + costs);
    }
    // If costs are explicitly 0, net and gross are identical
    const hasExplicitZeroCosts = [
      trade?.fees_commissions,
      trade?.totalCommissions,
      trade?.totalFees,
      trade?.commission,
      trade?.commissions,
      trade?.comm,
      trade?.fees,
      trade?.fee,
    ].some((f) => f != null && f !== '' && Number(f) === 0);

    if (hasExplicitZeroCosts) {
      return roundToCurrency(realizedNet);
    }
  }

  return null;
}

export function getTradeGrossPnl(trade: AnyTrade): number | null {
  return resolveTradeGrossPnl(trade);
}

export function resolveTradeRealizedPnl(trade: AnyTrade): number | null {
  if (normalizeTradeStatus(trade) === 'open') return null;

  const priceBasedPnl = getTradePriceBasedPnl(trade);
  let legacyFieldPnl: number | null = null;
  let authoritativeNetPnl: number | null = null;

  for (const field of REALIZED_PNL_FIELDS) {
    const candidate = trade?.[field];
    if (candidate == null || candidate === '') continue;
    const numeric = Number(candidate);
    if (!Number.isFinite(numeric)) continue;

    const rounded = roundToCurrency(numeric);
    if (isAuthoritativeNetField(field)) {
      if (authoritativeNetPnl == null || (authoritativeNetPnl === 0 && rounded !== 0)) {
        authoritativeNetPnl = rounded;
      }
    } else {
      if (legacyFieldPnl == null || (legacyFieldPnl === 0 && rounded !== 0)) {
        legacyFieldPnl = rounded;
      }
    }
  }

  if (authoritativeNetPnl != null) {
    if (authoritativeNetPnl === 0) {
      if (priceBasedPnl != null && priceBasedPnl !== 0) return priceBasedPnl;
      if (legacyFieldPnl != null && legacyFieldPnl !== 0) return legacyFieldPnl;
    }
    return authoritativeNetPnl;
  }

  return priceBasedPnl ?? legacyFieldPnl;
}

export function getTradePnl(trade: AnyTrade): number {
  const realized = resolveTradeRealizedPnl(trade);
  if (realized != null) return realized;
  return 0;
}

export function getTradeVolume(trade: AnyTrade): number {
  const candidates = [trade?.volume, trade?.qty, trade?.quantity, trade?.size];
  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) return numeric;
  }
  return 0;
}

export function extractTradeCost(trade: Record<string, any>): number {
  if (!trade || typeof trade !== 'object') return 0;

  const explicitFeesComm = Number(trade?.fees_commissions);
  if (Number.isFinite(explicitFeesComm) && explicitFeesComm > 0) return Math.abs(explicitFeesComm);

  const explicitTotalComm = Number(trade?.totalCommissions);
  if (Number.isFinite(explicitTotalComm) && explicitTotalComm > 0) return Math.abs(explicitTotalComm);

  const explicitTotalFees = Number(trade?.totalFees);
  if (Number.isFinite(explicitTotalFees) && explicitTotalFees > 0) return Math.abs(explicitTotalFees);

  const commCandidate = trade?.commission ?? trade?.commissions ?? trade?.comm;
  const feeCandidate = trade?.fees ?? trade?.fee;

  const comm = commCandidate != null && commCandidate !== '' ? Number(commCandidate) : null;
  const fee = feeCandidate != null && feeCandidate !== '' ? Number(feeCandidate) : null;

  const validComm = comm != null && Number.isFinite(comm) ? comm : null;
  const validFee = fee != null && Number.isFinite(fee) ? fee : null;

  if (validComm != null && validFee != null) {
    if (validComm === validFee) return Math.abs(validComm);
    return Math.abs(validComm) + Math.abs(validFee);
  }

  return Math.abs(validComm ?? validFee ?? 0);
}

/**
 * Resolve commission-only cost where the record separates it. Some providers
 * expose only a combined commission/fee total; in that case retain that
 * reported total rather than inventing a split.
 */
export function extractTradeCommission(trade: Record<string, any>): number {
  if (!trade || typeof trade !== 'object') return 0;

  for (const field of ['totalCommissions', 'total_commissions', 'commission', 'commissions', 'comm']) {
    const candidate = trade[field];
    if (candidate == null || candidate === '') continue;
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) return Math.abs(numeric);
  }

  // Combined-only broker exports cannot be reliably split into fees and
  // commissions. Preserve their recorded cost as the best available value.
  return extractTradeCost(trade);
}

export function extractTradeSwap(trade: Record<string, any>): number {
  if (!trade || typeof trade !== 'object') return 0;
  const candidates = [trade?.swap, trade?.swaps, trade?.interest, trade?.overnightFee];
  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) return Math.abs(numeric);
  }
  return 0;
}

export function normalizeTradeStatus(trade: AnyTrade): 'open' | 'closed' {
  if (!trade || typeof trade !== 'object') return 'closed';
  const row = trade as Record<string, any>;
  const rawStatus = String(row.status ?? '').trim().toLowerCase();

  if (OPEN_STATUS_VALUES.has(rawStatus)) return 'open';
  if (CLOSED_STATUS_VALUES.has(rawStatus)) return 'closed';

  const openQuantity = Number(row.openQuantity ?? row.openQty ?? row.remainingQuantity ?? row.remainingQty ?? row.positionSize ?? row.netPosition ?? row.net_position);
  if (Number.isFinite(openQuantity) && Math.abs(openQuantity) > 0) return 'open';

  const executions = Array.isArray(row.executionsList) ? row.executionsList : (Array.isArray(row.executions) ? row.executions : []);
  if (executions.length === 1) {
    return 'open';
  }
  if (executions.length > 1) {
    const hasBuy = executions.some((e: any) => String(e?.side ?? '').toLowerCase() === 'buy');
    const hasSell = executions.some((e: any) => String(e?.side ?? '').toLowerCase() === 'sell');
    if (!hasBuy || !hasSell) return 'open';
  }

  const hasExitPriceField = EXIT_PRICE_FIELDS.some((field) => hasOwnField(row, field));
  const hasCloseTimeField = CLOSE_TIME_FIELDS.some((field) => hasOwnField(row, field));

  if (!hasExitPriceField && !hasCloseTimeField) {
    const hasEntryFields = Boolean(
      row.symbol || row.openTimestampMs || row.openTime || row.entryPrice || row.price || row.avgEntryPrice || row.avgEntry || (executions && executions.length > 0)
    );
    if (!hasEntryFields) {
      return 'closed';
    }
    return 'open';
  }

  if ((hasExitPriceField && !hasFiniteField(row, EXIT_PRICE_FIELDS)) || (hasCloseTimeField && !hasUsableCloseTime(row))) {
    return 'open';
  }

  if (hasBlankField(row, EXIT_PRICE_FIELDS) || hasBlankField(row, CLOSE_TIME_FIELDS)) {
    return 'open';
  }

  return 'closed';
}

export function normalizeTradeSide(trade: AnyTrade): 'LONG' | 'SHORT' | '' {
  const raw = String(trade?.side ?? '').trim().toUpperCase();
  if (raw === 'LONG' || raw === 'BUY') return 'LONG';
  if (raw === 'SHORT' || raw === 'SELL') return 'SHORT';
  return '';
}

export function sortTradesNewestFirst<T extends AnyTrade>(trades: T[]): T[] {
  return [...trades].sort((left, right) => {
    const leftTs = getTradeTimestampMs(left) ?? 0;
    const rightTs = getTradeTimestampMs(right) ?? 0;
    if (rightTs !== leftTs) return rightTs - leftTs;

    const leftId = Number(left?.id ?? left?.storageId ?? 0);
    const rightId = Number(right?.id ?? right?.storageId ?? 0);
    return rightId - leftId;
  });
}

export function loadAuthoritativeTrades(): AnyTrade[] {
  const importedTrades = normalizeArray(loadImportedTrades());
  const customTrades = loadCustomTrades();
  return sortTradesNewestFirst(
    [...importedTrades, ...customTrades].map((trade) => ({
      ...trade,
      status: normalizeTradeStatus(trade),
    }))
  );
}

function aggregateTradesByDateKey(
  trades: AnyTrade[],
  options?: { includeOpen?: boolean },
): Map<string, AggregatedDay> {
  const aggregates = new Map<string, AggregatedDay>();
  const includeOpen = options?.includeOpen === true;

  trades.forEach((trade) => {
    const status = normalizeTradeStatus(trade);
    if (status === 'open' && !includeOpen) return;

    const dateKey = getTradeDateKey(trade);
    if (!dateKey) return;

    // Realized P&L semantics:
    //   - closed trades contribute their canonical realized P&L;
    //   - open trades contribute $0 to realized Calendar P&L (no fabricated
    //     exit price/duration). They are still counted as activity.
    const realizedPnl = resolveTradeRealizedPnl(trade);
    if (realizedPnl == null && status !== 'open') return;
    const effectivePnl = status === 'open' ? 0 : (realizedPnl ?? 0);

    const current = aggregates.get(dateKey) ?? { pnl: 0, trades: 0, volume: 0, wins: 0, losses: 0 };
    current.pnl += effectivePnl;
    current.trades += 1;
    current.volume += getTradeVolume(trade);
    addWinLossCount(current, effectivePnl);
    aggregates.set(dateKey, current);
  });

  return aggregates;
}

export function filterTradesByDateKey<T extends AnyTrade>(trades: T[], dateKey: string): T[] {
  return sortTradesNewestFirst(trades.filter((trade) => getTradeDateKey(trade) === dateKey));
}

export function filterTradesByTrailingDays<T extends AnyTrade>(trades: T[], days: number, endDate = new Date()): T[] {
  if (!Array.isArray(trades) || trades.length === 0 || days <= 0) return [];

  const end = startOfLocalDay(endDate).getTime() + DAY_MS - 1;
  const start = startOfLocalDay(endDate).getTime() - ((days - 1) * DAY_MS);

  return sortTradesNewestFirst(
    trades.filter((trade) => {
      const timestampMs = getTradeTimestampMs(trade);
      return timestampMs != null && timestampMs >= start && timestampMs <= end;
    })
  );
}

export function buildDailyDataFromTrades(trades: AnyTrade[], days: number, endDate = new Date()): DailyData[] {
  if (!Array.isArray(trades) || trades.length === 0 || days <= 0) return [];

  const end = startOfLocalDay(endDate);
  const start = new Date(end);
  start.setDate(end.getDate() - days + 1);

  const rangeTrades = trades.filter((trade) => {
    const timestampMs = getTradeTimestampMs(trade);
    return timestampMs != null && timestampMs >= start.getTime() && timestampMs <= end.getTime() + DAY_MS - 1;
  });

  const aggregates = aggregateTradesByDateKey(rangeTrades);
  const data: DailyData[] = [];
  let cumulative = 0;

  for (let current = new Date(start); current <= end; current = new Date(current.getTime() + DAY_MS)) {
    const dateKey = toDateKey(current);
    const aggregate = aggregates.get(dateKey) ?? { pnl: 0, trades: 0, volume: 0, wins: 0, losses: 0 };
    cumulative += aggregate.pnl;
    data.push({
      date: dateKey,
      pnl: aggregate.pnl,
      cum: cumulative,
      volume: aggregate.volume,
      winRate: calculateWinRatePercent(aggregate.wins, aggregate.losses),
      trades: aggregate.trades,
      wins: aggregate.wins,
      losses: aggregate.losses,
      breakeven: Math.max(0, aggregate.trades - aggregate.wins - aggregate.losses),
    });
  }

  return data;
}

export function buildWeekSummaryFromTrades(trades: AnyTrade[], referenceDate = new Date()) {
  const end = startOfLocalDay(referenceDate);
  const weekday = end.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const start = new Date(end);
  start.setDate(end.getDate() + mondayOffset);

  const aggregates = aggregateTradesByDateKey(trades);

  return Array.from({ length: 7 }, (_, index) => {
    const fullDate = new Date(start);
    fullDate.setDate(start.getDate() + index);
    const dateKey = toDateKey(fullDate);
    const aggregate = aggregates.get(dateKey) ?? { pnl: 0, trades: 0, volume: 0, wins: 0, losses: 0 };

    return {
      date: fullDate.getDate(),
      day: fullDate.toLocaleDateString('en-US', { weekday: 'short' }),
      pnl: aggregate.pnl,
      trades: aggregate.trades,
      fullDate,
    };
  });
}

export function buildMonthGridFromTrades(trades: AnyTrade[], year: number, month: number): MonthCell[] {
  const start = new Date(year, month, 1);
  const startDow = start.getDay();
  const gridStart = new Date(year, month, 1 - startDow);
  // Calendar counts every tenant-visible trade (open + closed) as activity.
  const aggregates = aggregateTradesByDateKey(trades, { includeOpen: true });
  const cells: MonthCell[] = [];

  for (let index = 0; index < 42; index += 1) {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    const aggregate = aggregates.get(toDateKey(day)) ?? { pnl: 0, trades: 0, volume: 0, wins: 0, losses: 0 };
    cells.push({
      d: day,
      inMonth: day.getMonth() === month,
      stat: {
        pnl: aggregate.pnl,
        trades: aggregate.trades,
        volume: aggregate.volume,
        winRate: calculateWinRatePercent(aggregate.wins, aggregate.losses),
      },
    });
  }

  while (cells.length > 0) {
    const lastWeek = cells.slice(-7);
    if (lastWeek.every((cell) => !cell.inMonth)) {
      cells.splice(-7, 7);
    } else {
      break;
    }
  }

  return cells;
}

export function buildMonthRowsFromTrades(trades: AnyTrade[], year: number, month: number) {
  // Calendar counts every tenant-visible trade (open + closed) as activity.
  const aggregates = aggregateTradesByDateKey(trades, { includeOpen: true });
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, index) => {
    const date = new Date(year, month, index + 1);
    const aggregate = aggregates.get(toDateKey(date)) ?? { pnl: 0, trades: 0, volume: 0, wins: 0, losses: 0 };
    return {
      day: index + 1,
      pnl: aggregate.pnl,
      trades: aggregate.trades,
    };
  });
}

export function buildDailyCumulative(raw: Array<{ date: string | number | Date; pnl: number }>, assumeDelta = true) {
  const map = new Map<number, number>();

  raw.forEach((row) => {
    const timestampMs = normalizeTimestampToMs(row.date);
    if (timestampMs == null) return;
    const dayMs = startOfLocalDay(new Date(timestampMs)).getTime();
    map.set(dayMs, (map.get(dayMs) ?? 0) + coerceNumber(row.pnl));
  });

  const keys = [...map.keys()].sort((left, right) => left - right);
  if (keys.length === 0) return [];

  const start = keys[0];
  const end = keys[keys.length - 1];
  const out: { ts: number; value: number }[] = [];
  let cumulative = 0;

  for (let ts = start; ts <= end; ts += DAY_MS) {
    const value = map.get(ts) ?? 0;
    cumulative = assumeDelta ? cumulative + value : (map.has(ts) ? value : cumulative);
    out.push({ ts, value: cumulative });
  }

  return out;
}
